import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Sparkles, CheckCircle2, Send, SkipForward, Compass } from 'lucide-react';
import {
  conversationalLaunch,
  ConversationalLaunchResponse,
  ConversationalTurn,
} from '../../../services/smartStartApi';

interface Props {
  goalText: string;
  projectId: number | null;
  onLaunched: (result: ConversationalLaunchResponse) => void;
  onCancel: () => void;
}

interface ProgressStage {
  label: string;
  done: boolean;
  active: boolean;
}

const ConversationalLaunch: React.FC<Props> = ({ goalText, projectId, onLaunched, onCancel }) => {
  const [turns, setTurns] = useState<ConversationalTurn[]>([
    { role: 'user', content: goalText, ts: new Date().toISOString() },
  ]);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [pipelineName, setPipelineName] = useState<string | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<{ slot_key: string; prompt: string; why_asking?: string } | null>(null);
  const [inferredSummary, setInferredSummary] = useState<string>('');
  const [filledSlots, setFilledSlots] = useState<Record<string, string>>({});
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [launchStages, setLaunchStages] = useState<ProgressStage[] | null>(null);
  const [launching, setLaunching] = useState(false);

  const initRanRef = useRef(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const answerRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (initRanRef.current) return;
    initRanRef.current = true;
    void runTurn({ initial: true });
  }, []);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [turns, pendingQuestion, launchStages]);

  useEffect(() => {
    if (pendingQuestion && answerRef.current) {
      answerRef.current.focus();
    }
  }, [pendingQuestion]);

  const runTurn = async (opts: { initial?: boolean; userAnswer?: string; skip?: boolean }) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const conversationForBackend = turns.map((t) => ({
        role: t.role,
        content: t.content,
        slot_key: t.slot_key,
        ts: t.ts,
      }));

      const result = await conversationalLaunch({
        sessionId,
        goalText: opts.initial ? goalText : null,
        projectId,
        conversationHistory: conversationForBackend,
        userAnswer: opts.userAnswer ?? null,
        skipRemaining: opts.skip === true,
      });

      if (result.session_id && !sessionId) setSessionId(result.session_id);
      if (result.pipeline_name) setPipelineName(result.pipeline_name);
      if (result.inferred_summary) setInferredSummary(result.inferred_summary);
      if (result.filled_slots) setFilledSlots(result.filled_slots);

      if (result.no_match) {
        setErrorMsg(result.message || 'No matching workflow found.');
        setPendingQuestion(null);
        return;
      }

      if (result.ready) {
        await playLaunchSequence(result);
        return;
      }

      if (result.next_question) {
        setPendingQuestion(result.next_question);
        setTurns((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: result.next_question!.prompt,
            slot_key: result.next_question!.slot_key,
            why_asking: result.next_question!.why_asking,
            ts: new Date().toISOString(),
          },
        ]);
      }
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || err.message || 'Smart Router failed to respond.');
    } finally {
      setLoading(false);
    }
  };

  const playLaunchSequence = async (result: ConversationalLaunchResponse) => {
    setLaunching(true);
    const targetLabel = result.target_step_label || result.target_screen || 'workflow';
    const stages: ProgressStage[] = [
      { label: `Picked workflow: ${result.pipeline_name || result.pipeline_key}`, done: false, active: true },
      { label: 'Building your plan…', done: false, active: false },
      { label: result.upload_followup
          ? `Opening upload screen for "${targetLabel}"`
          : `Redirecting to ${targetLabel}…`, done: false, active: false },
    ];
    setLaunchStages(stages);
    setPendingQuestion(null);

    await sleep(550);
    setLaunchStages((s) => s && [{ ...s[0], done: true, active: false }, { ...s[1], active: true }, s[2]]);
    await sleep(700);
    setLaunchStages((s) => s && [s[0], { ...s[1], done: true, active: false }, { ...s[2], active: true }]);
    await sleep(450);
    setLaunchStages((s) => s && [s[0], s[1], { ...s[2], done: true }]);
    await sleep(250);

    onLaunched(result);
  };

  const handleSendAnswer = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!answer.trim() || loading || launching) return;
    const trimmed = answer.trim();
    setTurns((prev) => [
      ...prev,
      { role: 'user', content: trimmed, ts: new Date().toISOString() },
    ]);
    setAnswer('');
    setPendingQuestion(null);
    void runTurn({ userAnswer: trimmed });
  };

  const handleSkip = () => {
    if (loading || launching) return;
    setTurns((prev) => [
      ...prev,
      { role: 'user', content: '(skip remaining questions — use sensible defaults)', ts: new Date().toISOString() },
    ]);
    setPendingQuestion(null);
    void runTurn({ skip: true });
  };

  const filledChips = Object.entries(filledSlots).filter(([, v]) => v && v.length > 0);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-lavender/10 rounded-lg">
            <Compass className="w-4 h-4 text-lavender" />
          </div>
          <div>
            <div className="text-sm font-bold text-gray-900">Smart Router conversation</div>
            {pipelineName && (
              <div className="text-[11px] text-gray-500">
                Routing to <span className="font-semibold text-lavender">{pipelineName}</span>
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          disabled={launching}
          className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>

      <div
        ref={scrollerRef}
        className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 max-h-[55vh] overflow-y-auto"
      >
        {turns.map((t, i) => (
          <div key={i} className={`flex mb-4 ${t.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] ${t.role === 'user' ? '' : 'flex items-start gap-2'}`}>
              {t.role === 'assistant' && (
                <div className="p-1.5 bg-lavender/10 rounded-md flex-shrink-0 mt-0.5">
                  <Sparkles className="w-3.5 h-3.5 text-lavender" />
                </div>
              )}
              <div
                className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  t.role === 'user'
                    ? 'bg-lavender text-white rounded-tr-sm'
                    : 'bg-gray-100 text-gray-900 rounded-tl-sm'
                }`}
              >
                <div className="whitespace-pre-wrap">{t.content}</div>
                {t.role === 'assistant' && t.why_asking && (
                  <div className="mt-1.5 text-[11px] text-gray-600 italic border-t border-gray-200 pt-1.5">
                    {t.why_asking}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {loading && !launching && (
          <div className="flex items-center gap-2 text-xs text-gray-500 italic">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Smart Router is thinking…
          </div>
        )}

        {launchStages && (
          <div className="mt-2 p-3 bg-gradient-to-br from-lavender/5 to-emerald-50 border border-lavender/20 rounded-xl">
            <div className="text-[11px] uppercase tracking-wide font-bold text-lavender mb-2">
              Launching
            </div>
            <ul className="space-y-1.5">
              {launchStages.map((stage, i) => (
                <li key={i} className="flex items-center gap-2 text-xs">
                  {stage.done ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                  ) : stage.active ? (
                    <Loader2 className="w-3.5 h-3.5 text-lavender animate-spin flex-shrink-0" />
                  ) : (
                    <div className="w-3.5 h-3.5 rounded-full border border-gray-300 flex-shrink-0" />
                  )}
                  <span className={stage.done ? 'text-gray-700' : stage.active ? 'text-lavender-deep font-medium' : 'text-gray-400'}>
                    {stage.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {errorMsg && (
          <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
            {errorMsg}
          </div>
        )}
      </div>

      {filledChips.length > 0 && !launching && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {filledChips.map(([k, v]) => (
            <span
              key={k}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded-full text-[11px] text-emerald-800"
              title={`${k}: ${v}`}
            >
              <CheckCircle2 className="w-2.5 h-2.5" />
              <span className="font-medium">{k}:</span>
              <span className="truncate max-w-[180px]">{v}</span>
            </span>
          ))}
        </div>
      )}

      {pendingQuestion && !launching && (
        <form onSubmit={handleSendAnswer} className="mt-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm focus-within:border-lavender focus-within:ring-2 focus-within:ring-lavender/20 transition">
            <textarea
              ref={answerRef}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  handleSendAnswer();
                }
              }}
              rows={2}
              placeholder="Type your answer…"
              disabled={loading}
              className="w-full p-3 text-sm bg-transparent border-0 focus:outline-none resize-none rounded-2xl placeholder:text-gray-400"
            />
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-gray-100">
              <button
                type="button"
                onClick={handleSkip}
                disabled={loading}
                className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50"
                title="Skip remaining questions and launch with sensible defaults"
              >
                <SkipForward className="w-3.5 h-3.5" />
                Skip — use defaults
              </button>
              <button
                type="submit"
                disabled={!answer.trim() || loading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-lavender text-white text-xs font-semibold rounded-lg hover:bg-lavender-deep transition disabled:opacity-40 disabled:cursor-not-allowed"
                title="Send (⌘/Ctrl + Enter)"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (
                  <>
                    Send
                    <Send className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      )}

      {inferredSummary && !launching && !pendingQuestion && !errorMsg && (
        <div className="mt-3 text-[11px] text-gray-500 italic px-2">
          {inferredSummary}
        </div>
      )}
    </div>
  );
};

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default ConversationalLaunch;
